import React, { useState, useEffect } from "react";
import Header from "../../components/header";
import Footer from "../../components/footer";
import { getProfile, editProfile, deleteUserAccount } from "../../api/user";
import { useAppSelector } from "../../state/hooks";
import { jwtDecode } from "jwt-decode";
import { countries } from "../../constants/countries";
import { toast } from "react-toastify";
import Input from "../../components/shared/Input";
import Button from "../../components/shared/Button";

const Section = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <div className="text-white mb-8 bg-[#0D0D0D] p-6 border border-[#3A3A3A]">
        <h2 className="text-2xl font-bold mb-4 text-white uppercase" style={{ fontFamily: '"Old English Text MT", serif', backgroundImage: 'url("/assets/img/landing/d.png")', backgroundSize: 'cover', backgroundPosition: 'center' }}>{title}</h2>
        <div>{children}</div>
    </div>
);

const UserInformation = ({ nickname, oldNickname, setNickname, handleChangeNickname }: { nickname: string, oldNickname: string, setNickname: (value: string) => void, handleChangeNickname: () => void }) => {
    return (
        <Section title="User Information">
            <p className="text-sm text-gray-400 mb-4 font-lato">You can change your nickname once every week, the first change will be free of charge, while the next rename attempts will require a fixed sum freemium and premium currencies.</p>
            <p className="text-sm text-gray-400 mb-4 font-lato">
                -Your nickname can't contain swear or racist words;<br/>
                -You can't use the nickname of a Staff member of iPlayMore;<br/>
                -Your unique ID can't be changed;<br/>
                -Being reported for using an inappropriate nickname will result in a penalty to your account based on the gravity of your nickname. Your nickname will be replaced with an automatically generated name until you change it; this change is free of charge.
            </p>
            <p className="text-sm text-gray-400 mb-4 font-lato font-bold">Rename cost: Free of charge!</p>
            <div className="space-y-4">
                <div>
                    <p className="text-white text-sm font-lato mb-1">Nickname</p>
                    <Input type="text" value={nickname} onChange={(value) => setNickname(value)} placeholder="Nickname" className="!h-[51px]" />
                </div>
                <div>
                    <Input type="text" value={oldNickname || "OldNickname"} onChange={() => {}} placeholder="OldNickname" className="!h-[51px]" />
                </div>
                <div className="flex justify-between">
                    <Button className="w-[98px] !h-[51px]">Back</Button>
                    <Button onClick={handleChangeNickname} className="w-[150px] !h-[51px]">Change Nickname</Button>
                </div>
            </div>
        </Section>
    );
}

const AccountSettings = ({ profileData }: { profileData: any }) => {
    const [email, setEmail] = useState("");
    const [country, setCountry] = useState("");
    const [birthday, setBirthday] = useState("");
    const [accountVisibility, setAccountVisibility] = useState<"public" | "private" | "friends">("public"); // Fixed type
    const [phoneNumber, setPhoneNumber] = useState("");
    const [receiveNews, setReceiveNews] = useState(false);

    useEffect(() => {
        if (profileData) {
            setEmail(profileData.email || "");
            setCountry(profileData.country || "Canada");
            setBirthday(profileData.birthday ? new Date(profileData.birthday).toISOString().split('T')[0] : "");
            setAccountVisibility(profileData.accountVisibility || "public");
            setPhoneNumber(profileData.phoneNumber || "");
            setReceiveNews(profileData.receiveNews || false);
        }
    }, [profileData]);

    const handleSaveChanges = async () => {
        const updatedProfile = { email, country, birthday, accountVisibility, phoneNumber, receiveNews };
        try {
            const response = await editProfile(updatedProfile);
            if (response) {
                toast.success("Account settings updated successfully!");
            } else {
                toast.error(response.message || "Failed to update account settings.");
            }
        } catch (error) {
            console.error("Error saving account settings:", error);
            toast.error("An error occurred while saving account settings.");
        }
    };

    return (
        <Section title="Account Settings">
            <div className="space-y-4">
                <div>
                    <label className="text-sm text-gray-400 font-lato">Email</label>
                    <Input type="email" value={email} onChange={(value) => setEmail(value)} className="!h-[51px]" />
                </div>
                <div>
                    <label className="text-sm text-gray-400 font-lato">Phone Number</label>
                    <Input type="tel" value={phoneNumber} onChange={(value) => setPhoneNumber(value)} className="!h-[51px]" />
                </div>
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/2">
                        <label className="text-sm text-gray-400 font-lato">Country</label>
                        <Input type="select" value={country} onChange={(value) => setCountry(value)} options={countries} className="!h-[51px]" />
                    </div>
                    <div className="w-full md:w-1/2">
                        <label className="text-sm text-gray-400 font-lato">Birthday</label>
                        <Input type="date" value={birthday} onChange={(value) => setBirthday(value)} className="!h-[51px]" />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-400 font-lato">Account Visibility</label>
                    <Input type="select" value={accountVisibility} onChange={(value) => setAccountVisibility(value)} options={[{value: "public", label: "Public"}, {value: "private", label: "Private"}, {value: "friends", label: "Friends Only"}]} className="!h-[51px]" />
                </div>
                <div className="flex items-center gap-2 mt-4">
                    <Input type="checkbox" checked={receiveNews} onChange={(value) => setReceiveNews(value)} className="h-5 w-5" />
                    <p className="text-sm text-gray-400 font-lato">I would like to receive iPlayMore news, special offers, releases and more.</p>
                </div>
                <div className="flex justify-end mt-4">
                    <Button onClick={handleSaveChanges} className="w-[150px] !h-[51px]">Save Changes</Button>
                </div>
            </div>
        </Section>
    );
}

const Inventory = () => {
    return (
        <Section title="Inventory">
            <div className="flex justify-between items-center mb-4">
                <p className="text-gray-300 font-lato">Total Items: 0</p>
                <Button className="w-[98px] !h-[51px]">Browse</Button>
            </div>
            <div className="flex justify-between items-center">
                <div>
                    <p className="text-gray-300 font-lato">Total slots: 0</p>
                    <p className="text-gray-300 font-lato">Available: 0</p>
                </div>
                <Button className="w-[98px] !h-[51px]">Add</Button>
            </div>
        </Section>
    );
}

const DoubleFactorAuthentication = () => {
    return (
        <Section title="Double-factor Authentication">
            <p className="text-sm text-gray-400 mb-4 font-lato">Activate the double factor Authentication to protect your account even more from hackers and data breaches.</p>
            <Button className="w-[120px] !h-[51px]">Activate</Button>
        </Section>
    );
}

const TwitchAccount = () => {
    return (
        <Section title="Twitch Account">
            <p className="text-sm text-gray-400 mb-4 font-lato">Link your account to obtain exclusive rewards by watching iPlayMore & Partners streams! If your Twitch account is eligible, you will be able to use our Streamer Tool to host unique giveaways!</p>
            <Button className="w-[150px] !h-[51px]">Link account</Button>
        </Section>
    );
}

const DeleteAccount = () => {
    const [showModal, setShowModal] = useState(false);
    const [password, setPassword] = useState("");

    const handleDeleteAccount = async () => {
        if (!password) {
            toast.error("Please enter your password to confirm deletion.");
            return;
        }
        try {
            const response = await deleteUserAccount(password);
            if (response.success) {
                toast.success(response.message);
                // Redirect to homepage or logout after successful deletion initiation
                // For now, just log out
                localStorage.removeItem("token");
                sessionStorage.removeItem("token");
                window.location.href = "/";
            } else {
                toast.error(response.message || "Failed to delete account.");
            }
        } catch (error) {
            console.error("Error deleting account:", error);
            toast.error("An error occurred during account deletion.");
        }
    };

    return (
        <>
            <Section title="Delete Account">
                <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-400 font-lato">Once you delete your account, it can’t be recovered.</p>
                    <Button onClick={() => setShowModal(true)} className="w-[150px] !h-[51px] bg-red-600 hover:bg-red-700">Delete Account</Button>
                </div>
            </Section>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
                    <div className="bg-[#0D0D0D] p-6 border border-[#3A3A3A] w-full max-w-lg text-white text-center">
                        <h2 className="text-2xl font-bold mb-4 text-white uppercase" style={{ fontFamily: '"Old English Text MT", serif' }}>Delete Account</h2>
                        <p className="text-sm text-gray-300 mb-2 font-lato">By Requesting Account Deletion, your account will be deleted in full in <span className="font-bold">14 days.</span></p>
                        <p className="text-sm text-gray-400 mb-2 font-lato">Within this 14 days, you will have the opportunity to reactivate your account in full.</p>
                        <p className="text-sm text-gray-400 mb-4 font-lato">After the a lot of days, your account will be permanently deleted, including any and all marketplace items you may have purchased since your account was created.</p>
                        <p className="text-xs text-gray-500 mb-4 font-lato">Note: any marketplace items owned by the account will become the sole property of IMU Studios, and any value of these items will be donated to a charity of IMU Studios choosing after the 14 days if the account has not been reactivated. By performing this action, the account owner agrees to these terms.</p>
                        
                        <div className="space-y-4 my-6">
                            <Input 
                                type="password" 
                                value={password}
                                onChange={(value) => setPassword(value)}
                                placeholder="Password" 
                                className="!h-[51px]" 
                            />
                        </div>

                        <div className="flex justify-between gap-4">
                            <Button onClick={() => setShowModal(false)} className="w-full !h-[51px]">Back</Button>
                            <Button onClick={handleDeleteAccount} className="w-full !h-[51px]">Send Confirmation Email</Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

interface CurrencyProps {
  name: string;
  description: string;
  icon: string;
  amount: number;
  showAddButton: boolean;
}

const Currency = ({ name, description, icon, amount, showAddButton }: CurrencyProps) => (
    <div className="text-white mb-8 bg-[#0D0D0D] p-6 border border-[#3A3A3A]">
        <div className="flex justify-between items-center">
            <div className="flex items-center">
                <img src={icon} alt={name} className="w-12 h-12 mr-4"/>
                <div>
                    <p className="font-bold text-lg">{name}</p>
                    <p className="text-sm text-gray-400 font-lato">{description}</p>
                </div>
            </div>
            <div className="flex items-center">
                <p className="font-bold text-2xl mr-4">{amount}</p>
                {showAddButton && <Button className="w-[98px] !h-[51px]">Add</Button>}
            </div>
        </div>
    </div>
);

const Currencies = () => {
    return (
        <div>
            <Currency
                name="Manna"
                description="Premium currency used to purchase gear, devices, scepters, and more through the marketplace."
                icon="/assets/img/manna-icon.png"
                amount={0}
                showAddButton={true}
            />
            <Currency
                name="Solar"
                description="Currency used to purchase rare items, minerals, and to rent gear from other players."
                icon="/assets/img/solar-icon.png"
                amount={0}
                showAddButton={false}
            />
            <Currency
                name="Lunar"
                description="Currency used to purchase common and uncommon resources from merchants in game."
                icon="/assets/img/lunar-icon.png"
                amount={0}
                showAddButton={false}
            />
        </div>
    );
}

const Profile = () => {
  const { nickname: initialNickname } = useAppSelector((state) => state.authSlice);
  const [nickname, setNickname] = useState<string>(initialNickname || ""); // Ensure string type
  const [oldNickname, setOldNickname] = useState<string>(""); // Ensure string type
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          jwtDecode(token);
          const profile = await getProfile(); // Removed userId argument
          setUserProfile(profile);
          setNickname(profile.nickName || initialNickname || ""); // Ensure string type
          setOldNickname(profile.nickName || initialNickname || ""); // Ensure string type
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      }
    };
    fetchUserProfile();
  }, [initialNickname]);

  const handleChangeNickname = async () => {
    if (!nickname.trim()) {
      toast.error("Nickname cannot be empty.");
      return;
    }
    try {
      const response = await editProfile({ nickName: nickname });
      if (response && response.nickName) {
        toast.success("Nickname updated successfully!");
        // Optionally, update the global state or refetch profile
      } else {
        toast.error(response.message || "Failed to update nickname.");
      }
    } catch (error) {
      console.error("Error updating nickname:", error);
      toast.error("An error occurred while updating nickname.");
    }
  };

  return (
    <div className={`w-full h-full flex flex-col bg-[#000000]`}>
      <Header />
      <div
        style={{
       backgroundImage: "url(/assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex flex-col items-center w-full min-h-screen relative pt-6 px-4"
      >
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Welcome, {nickname}</h1>

          <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <UserInformation nickname={nickname} oldNickname={oldNickname} setNickname={setNickname} handleChangeNickname={handleChangeNickname} />
              <AccountSettings profileData={userProfile} />
              <Inventory />
              <DoubleFactorAuthentication />
            </div>
            <div>
              <Currencies />
              <TwitchAccount />
              <DeleteAccount />
            </div>
          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default Profile;
